import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Search, ArrowUpRight } from "lucide-react";

const TIER_PILL = (color) => {
  if (color === "red" || color === "orange" || color === "amber") return "border-warn/40 bg-warn-soft text-warn";
  if (color === "light_green" || color === "dark_green") return "border-good/40 bg-good-soft text-good";
  return "border-ink-line bg-secondary text-ink-muted";
};
const TIERS = ["Low Risk Exposure", "Moderate Risk Exposure", "Elevated Risk Exposure", "High Risk Exposure", "Critical Risk Exposure"];

export default function PceiSubmissions() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("");
  const [status, setStatus] = useState("completed");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (tier) params.set("tier", tier);
      if (status) params.set("status", status);
      const { data } = await api.get(`/pcei/submissions?${params.toString()}`);
      setItems(data.items);
      setStats(data.stats);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tier, status]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div className="space-y-8" data-testid="pcei-submissions-page">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-ink-line pb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-warn text-white grid place-items-center rounded-sm">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Risk Exposure</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-1">PCEI Submissions</h1>
            <p className="text-sm text-ink-muted mt-1.5">All Project Complexity & Exposure Index attempts across tenders and team members.</p>
          </div>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card-flat p-4"><div className="stat-label">Completed</div><div className="font-num text-3xl font-bold mt-2" data-testid="pcei-stat-completed">{stats.completed}</div></div>
          <div className="card-flat p-4"><div className="stat-label">In progress</div><div className="font-num text-3xl font-bold mt-2 text-ink-muted">{stats.in_progress}</div></div>
          <div className="card-flat p-4"><div className="stat-label">Archived</div><div className="font-num text-3xl font-bold mt-2 text-ink-muted">{stats.archived}</div></div>
          <div className="card-flat p-4"><div className="stat-label">Avg risk</div><div className="font-num text-3xl font-bold mt-2 text-warn">{stats.avg_score || 0}</div></div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          {TIERS.map((t) => (
            <div key={t} className="card-flat p-3">
              <div className="stat-label text-[10px]">{t}</div>
              <div className="font-num text-xl font-bold mt-1">{stats.tier_counts?.[t] || 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="pcei-search"
            placeholder="Search by client, title, location, submitter…"
            className="w-full pl-9 pr-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" />
        </div>
        <div className="flex gap-0 border border-ink-line rounded-sm bg-white overflow-hidden">
          {[
            { k: "completed", l: "Completed" }, { k: "in_progress", l: "In progress" },
            { k: "archived", l: "Archived" }, { k: "all", l: "All" },
          ].map((s, i) => (
            <button key={s.k} onClick={() => setStatus(s.k)} data-testid={`pcei-status-${s.k}`}
              className={`px-3 py-2 text-[12px] uppercase tracking-wider ${i < 3 ? "border-r border-ink-line" : ""} ${status === s.k ? "bg-brand text-white font-medium" : "text-ink-muted hover:bg-secondary"}`}>
              {s.l}
            </button>
          ))}
        </div>
        <div className="flex gap-0 border border-ink-line rounded-sm bg-white overflow-hidden flex-wrap">
          <button onClick={() => setTier("")} className={`px-3 py-2 text-[12px] uppercase tracking-wider border-r border-ink-line ${tier === "" ? "bg-brand text-white font-medium" : "text-ink-muted hover:bg-secondary"}`}>All tiers</button>
          {TIERS.map((t, i) => (
            <button key={t} onClick={() => setTier(t)}
              className={`px-3 py-2 text-[12px] uppercase tracking-wider ${i < TIERS.length - 1 ? "border-r border-ink-line" : ""} ${tier === t ? "bg-brand text-white font-medium" : "text-ink-muted hover:bg-secondary"}`}>
              {t.replace(" Risk Exposure", "")}
            </button>
          ))}
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-ink-line">
            <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <th className="px-5 py-3 font-medium">Submitted</th>
              <th className="px-5 py-3 font-medium">By</th>
              <th className="px-5 py-3 font-medium">Client</th>
              <th className="px-5 py-3 font-medium">Tender</th>
              <th className="px-5 py-3 font-medium text-right">Score</th>
              <th className="px-5 py-3 font-medium">Tier</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={8} className="px-5 py-10 text-center text-ink-muted uppercase tracking-wider text-xs">Loading…</td></tr>)}
            {!loading && items.length === 0 && (<tr><td colSpan={8} className="px-5 py-10 text-center text-ink-muted">No PCEI submissions match.</td></tr>)}
            {items.map((s) => {
              const t = s.tender || {};
              return (
                <tr key={s.id} className="border-b border-ink-line last:border-b-0 hover:bg-secondary/60 transition-colors">
                  <td className="px-5 py-3 text-ink-muted font-num text-xs">{s.updated_at ? new Date(s.updated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                  <td className="px-5 py-3">{s.user_name || s.user_email || "—"}</td>
                  <td className="px-5 py-3 font-medium">{t.client || "—"}</td>
                  <td className="px-5 py-3 max-w-xs truncate" title={t.title}>{t.title || "—"}</td>
                  <td className="px-5 py-3 text-right font-num font-bold text-base">{s.total_score ?? "—"}</td>
                  <td className="px-5 py-3"><span className={`chip ${TIER_PILL(s.tier_color)}`}>{s.tier_label || "—"}</span></td>
                  <td className="px-5 py-3 text-ink-muted text-xs uppercase tracking-wider">{s.status}</td>
                  <td className="px-5 py-3 text-right">
                    <Link to={`/admin/pcei/${s.id}`} data-testid={`pcei-view-submission-${s.id}`}
                      className="inline-flex items-center gap-1 text-brand hover:text-brand-hover text-[13px] font-medium">
                      View <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
